import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../App';
import Layout from '../components/Layout';
import { FileText, Eye, FolderOpen } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const AnalyticsPage = ({ user, setUser }) => {
  const [analytics, setAnalytics] = useState(null);
  const [documentAnalytics, setDocumentAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [overview, docAnalytics] = await Promise.all([
        axios.get(`${API_URL}/analytics/overview`),
        axios.get(`${API_URL}/analytics/documents`)
      ]);
      setAnalytics(overview.data);
      setDocumentAnalytics(docAnalytics.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-screen">
          <div className="text-lg text-zinc-600">Loading analytics...</div>
        </div>
      </Layout>
    );
  }

  const categoryData = documentAnalytics?.by_category?.map(item => ({
    name: item._id || 'Uncategorized',
    count: item.count
  })) || [];

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-8" data-testid="analytics-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
            Analytics Dashboard
          </h1>
          <p className="text-zinc-600" style={{ fontFamily: 'Inter, sans-serif' }}>
            Insights into your knowledge base usage
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="total-documents-stat">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-violet-100 flex items-center justify-center">
                <FileText className="h-6 w-6 text-violet-600" />
              </div>
              <div>
                <div className="text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {analytics?.total_documents || 0}
                </div>
                <div className="text-sm text-zinc-600">Total Documents</div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="total-users-stat">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-teal-100 flex items-center justify-center">
                <FolderOpen className="h-6 w-6 text-teal-600" />
              </div>
              <div>
                <div className="text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {analytics?.total_users || 0}
                </div>
                <div className="text-sm text-zinc-600">Team Members</div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="total-chats-stat">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-zinc-100 flex items-center justify-center">
                <Eye className="h-6 w-6 text-zinc-600" />
              </div>
              <div>
                <div className="text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {analytics?.total_chats || 0}
                </div>
                <div className="text-sm text-zinc-600">AI Conversations</div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Documents by Category */}
          <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="category-chart">
            <h2 className="text-xl font-bold text-zinc-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Documents by Category
            </h2>
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={categoryData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="name" stroke="#71717a" />
                  <YAxis stroke="#71717a" />
                  <Tooltip />
                  <Bar dataKey="count" fill="#7c3aed" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-zinc-500">
                No data available
              </div>
            )}
          </div>

          {/* Most Viewed Documents */}
          <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="most-viewed-section">
            <h2 className="text-xl font-bold text-zinc-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Most Viewed Documents
            </h2>
            <div className="space-y-3">
              {documentAnalytics?.most_viewed?.slice(0, 10).map((doc, index) => (
                <div key={index} className="flex items-center justify-between p-3 border border-zinc-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-violet-100 flex items-center justify-center text-violet-600 font-bold text-sm">
                      {index + 1}
                    </div>
                    <span className="font-medium text-zinc-900">{doc.title}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-zinc-600">
                    <Eye className="h-4 w-4" />
                    <span>{doc.views}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AnalyticsPage;